<script lang="ts">
  import Modal from "@shared/ui/Modal.svelte";
  import { archiveContract } from "@entities/contract/store.svelte";
  import type { Contract } from "@domain/contract/model";

  interface Props {
    open: boolean;
    contract: Contract;
    onClose: () => void;
    onArchived: (contract: Contract) => void;
  }

  let { open, contract, onClose, onArchived }: Props = $props();

  let reason = $state("");
  let submitting = $state(false);
  let serverError = $state<string | null>(null);
  let validationError = $state<string | null>(null);

  let contractLabel = $derived(
    contract.deal_name ?? contract.contract_type ?? contract.id,
  );

  $effect(() => {
    if (open) {
      reason = "";
      serverError = null;
      validationError = null;
    }
  });

  function close() {
    onClose();
  }

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    if (reason.trim().length === 0) {
      validationError = "アーカイブ理由を入力してください";
      return;
    }
    validationError = null;
    submitting = true;
    serverError = null;
    try {
      const updated = await archiveContract(contract.id, reason.trim());
      onArchived(updated);
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal {open} title="契約をアーカイブ" onClose={close}>
  <form id="contract-archive-form" onsubmit={handleSubmit}>
    <p class="target">
      対象契約: <strong>{contractLabel}</strong>
    </p>
    <ul class="notes">
      <li>通常一覧、タイムライン、集計から非表示になります。</li>
      <li>契約データは削除されず、後で復元できます。</li>
    </ul>
    <label>
      <span>アーカイブ理由 <em>*</em></span>
      <textarea
        bind:value={reason}
        rows="3"
        required
        placeholder="例: 重複登録のため"
      ></textarea>
    </label>
    {#if validationError}<p class="server-error">{validationError}</p>{/if}
    {#if serverError}<p class="server-error">{serverError}</p>{/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}>
      キャンセル
    </button>
    <button
      type="submit"
      form="contract-archive-form"
      class="primary"
      disabled={submitting || reason.trim().length === 0}
    >
      {submitting ? "アーカイブ中…" : "アーカイブ"}
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  span em {
    color: #b91c1c;
    font-style: normal;
  }
  textarea {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
    resize: vertical;
  }
  .target {
    margin: 0;
    color: #374151;
  }
  .notes {
    margin: 0;
    padding-left: 18px;
    color: #4b5563;
    font-size: 0.875rem;
  }
  .server-error {
    color: #b91c1c;
    margin: 0;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #b45309;
    border-color: #b45309;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
